import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useParams, useNavigate, Link } from "react-router-dom";
import TablePagination from "./TablePagination";

const ParticipationHistoryLayer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        // Simulate fetching client participation data based on ID
        setTimeout(() => {
            const allClientData = {
                "c1": { 
                    companyName: "Tech Solutions", 
                    fullName: "Alice Green",
                    participatedExpos: [
                        { id: 101, name: "Modern Home Expo 2024", location: "Coimbatore", date: "15-01-2024", product: "Cloud Systems", rating: 5, feedback: "Excellent footfall and premium leads." },
                        { id: 102, name: "Global Trade Fair 2026", location: "Chennai", date: "10-03-2026", product: "AI Dashboard", rating: 4, feedback: "Strong networking with international buyers." }
                    ]
                },
                "c2": { 
                    companyName: "Solar Systems", 
                    fullName: "Bob White",
                    participatedExpos: [
                        { id: 201, name: "Green Energy Fair 2025", location: "Bangalore", date: "05-02-2025", product: "Solar Panel X1", rating: 4, feedback: "Met many qualified prospects for residential install." }
                    ]
                }
            };
            
            // If ID not found in mock, try to find in localStorage as a fallback
            const storedMembers = JSON.parse(localStorage.getItem("members") || "[]");
            const memberInfo = storedMembers.find(m => (m._id || m.id) === id);

            setClient(allClientData[id] || { 
                companyName: memberInfo?.companyName || "Member Profile", 
                fullName: memberInfo?.fullName || "Not Specified",
                participatedExpos: memberInfo?.participatedExpos > 0 ? [
                    { id: 999, name: "Past Exhibition 2023", location: "Salem", date: "12-10-2023", product: "Standard Product", rating: 4, feedback: "Positive engagement" }
                ] : [] 
            });
            setLoading(false);
        }, 500);
    }, [id]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (e) => {
        setRowsPerPage(parseInt(e.target.value));
        setCurrentPage(0);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-100">
                <Icon icon="line-md:loading-twotone-loop" className="text-danger text-xxl" />
            </div>
        );
    }

    const totalRecords = client.participatedExpos?.length || 0;
    const totalPages = Math.ceil(totalRecords / rowsPerPage);
    const paginatedExpos = (client.participatedExpos || []).slice(
        currentPage * rowsPerPage,
        (currentPage + 1) * rowsPerPage
    );

    return (
        <div className="card h-100 p-0 radius-12 border-0 shadow-sm overflow-hidden">
            <div 
                className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between"
                style={{ background: "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)" }}
            >
                <div>
                    <h6 className="text-white mb-0 fw-bold">Participation History</h6>
                    <span className="text-white opacity-75 text-sm">{client.companyName} | {client.fullName}</span>
                </div>
                <button 
                    onClick={() => navigate(-1)} 
                    className="btn btn-sm d-flex align-items-center gap-2"
                    style={{ backgroundColor: "#f7e7da", color: "black", fontWeight: "600" }}
                >
                    <Icon icon="solar:arrow-left-bold" /> Back
                </button>
            </div>
            
            <div className="card-body p-24">
                <div className="table-responsive radius-12 border shadow-sm bg-white overflow-x-auto">
                    <table className="table bordered-table table-hover mb-0 sm-table" style={{ minWidth: '1000px' }}>
                        <thead>
                            <tr style={{ background: "linear-gradient(135deg, #bb0007, #ed1c24)" }}>
                                <th scope="col" className="fw-bold text-white text-xs py-12 px-16">S.No</th>
                                <th scope="col" className="fw-bold text-white text-xs py-12 px-16">Expo Name</th>
                                <th scope="col" className="fw-bold text-white text-xs py-12 px-16">Location</th>
                                <th scope="col" className="fw-bold text-white text-xs py-12 px-16">Date</th>
                                <th scope="col" className="fw-bold text-white text-xs py-12 px-16">Product</th>
                                <th scope="col" className="fw-bold text-white text-xs py-12 px-16">Ratings</th>
                                <th scope="col" className="fw-bold text-white text-xs py-12 px-16">Feedback</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedExpos.length > 0 ? (
                                paginatedExpos.map((expo, index) => (
                                    <tr key={expo.id}>
                                        <td className="px-16 py-12">{currentPage * rowsPerPage + index + 1}</td>
                                        <td className="px-16 py-12">
                                            <div className="fw-bold text-primary-600 text-sm">{expo.name}</div>
                                        </td>
                                        <td className="px-16 py-12 text-sm">{typeof expo.location === 'object' ? (expo.location?.address || JSON.stringify(expo.location)) : (expo.location || "-")}</td>
                                        <td className="px-16 py-12 text-sm">{expo.date}</td>
                                        <td className="px-16 py-12">
                                            <span className="badge bg-danger-focus text-danger-main radius-4 text-xs">{expo.product}</span>
                                        </td>
                                        <td className="px-16 py-12">
                                            <div className="d-flex align-items-center gap-1">
                                                {[...Array(5)].map((_, i) => (
                                                    <Icon 
                                                        key={i} 
                                                        icon="solar:star-bold" 
                                                        className={i < expo.rating ? "text-warning" : "text-neutral-200"} 
                                                        style={{ fontSize: '14px' }}
                                                    />
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-16 py-12">
                                            <p className="mb-0 text-xs text-secondary-light italic" style={{ maxWidth: '250px' }}>
                                                "{expo.feedback}"
                                            </p>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center py-40">
                                        <div className="text-secondary-light">No participation records found for this client.</div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="mt-24">
                  <TablePagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    totalRecords={totalRecords}
                  />
                </div>
            </div>
        </div>
    );
};

export default ParticipationHistoryLayer;
